import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Alert,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useAuth } from "../auth/AuthContext";
import { getBillingStatus, createPortalSession, rotateApiKey, BillingStatus } from "../data/request";
import { DELTA_PRIMARY, DELTA_ACCENT, TEXT_SECONDARY } from "./colors";

const PLAN_LABEL: Record<BillingStatus["web_plan"], string> = { free: "Free", pro: "Pro", enterprise: "Enterprise" };
const API_PLAN_LABEL: Record<BillingStatus["api_plan"], string> = {
  none: "None",
  api_pro: "API Pro",
  api_enterprise: "API Enterprise",
};

export const BillingPage = () => {
  const auth = useAuth();
  const [status, setStatus] = useState<BillingStatus>();
  const [loading, setLoading] = useState(true);
  const [portalSubmitting, setPortalSubmitting] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (auth.status !== "signedIn") {
      return;
    }
    getBillingStatus()
      .then((doc) =>
        setStatus(
          doc ?? {
            web_plan: "free",
            web_status: null,
            web_billing_cycle: null,
            api_plan: "none",
            api_status: null,
            api_key_last4: null,
          }
        )
      )
      .finally(() => setLoading(false));
  }, [auth.status]);

  if (auth.status === "loading" || (auth.status === "signedIn" && loading)) {
    return (
      <Box style={{ padding: 40, textAlign: "center" }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (auth.status !== "signedIn") {
    return (
      <Box style={{ padding: 40, textAlign: "center" }}>
        <Typography variant="h6" style={{ color: DELTA_PRIMARY, fontWeight: 700, marginBottom: 8 }}>
          Sign in or sign up to continue
        </Typography>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 24 }}>
          You'll need an account to manage billing or subscribe to a plan.
        </Typography>
        <Box style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Button component={RouterLink} to="/login" variant="outlined" style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}>
            Sign In
          </Button>
          <Button component={RouterLink} to="/login?mode=signUp" variant="contained" style={{ backgroundColor: DELTA_ACCENT }}>
            Sign Up
          </Button>
        </Box>
      </Box>
    );
  }

  const handleManageBilling = async () => {
    setPortalSubmitting(true);
    setError(undefined);
    const doc = await createPortalSession();
    if (doc?.url) {
      window.location.href = doc.url;
      return;
    }
    setError(doc?.error ?? "Couldn't open the billing portal. Please try again.");
    setPortalSubmitting(false);
  };

  const handleConfirmRotate = async () => {
    setRotating(true);
    setError(undefined);
    const doc = await rotateApiKey();
    setRotating(false);
    setRotateOpen(false);
    if (doc?.api_key) {
      setNewApiKey(doc.api_key);
      const refreshed = await getBillingStatus();
      setStatus(refreshed);
      return;
    }
    setError(doc?.error ?? "Couldn't rotate the API key. Please try again.");
  };

  const hasSubscription = Boolean(status?.web_status) || Boolean(status?.api_status);

  return (
    <Box style={{ maxWidth: 640 }}>
      <Typography variant="h4" style={{ color: DELTA_PRIMARY, marginBottom: 24 }}>
        Billing
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} style={{ padding: 24, marginBottom: 24 }}>
        <Typography variant="h6" style={{ color: DELTA_PRIMARY, marginBottom: 8 }}>
          Plan
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 4 }}>
          {PLAN_LABEL[status?.web_plan ?? "free"]}
          {status?.web_billing_cycle && ` (${status.web_billing_cycle})`}
        </Typography>
        {status?.web_status && (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 16 }}>
            Status: {status.web_status}
          </Typography>
        )}
        {hasSubscription ? (
          <Button
            variant="outlined"
            disabled={portalSubmitting}
            onClick={handleManageBilling}
            style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
          >
            {portalSubmitting ? "Redirecting..." : "Manage Billing"}
          </Button>
        ) : (
          <Button component={RouterLink} to="/" variant="outlined" style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}>
            View Plans
          </Button>
        )}
      </Paper>

      <Paper elevation={0} style={{ padding: 24 }}>
        <Typography variant="h6" style={{ color: DELTA_PRIMARY, marginBottom: 8 }}>
          API Access
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 4 }}>
          {API_PLAN_LABEL[status?.api_plan ?? "none"]}
        </Typography>

        {status?.api_key_last4 ? (
          <>
            <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 16 }}>
              API key: ••••••••{status.api_key_last4}
              {status.api_status && ` (${status.api_status})`}
            </Typography>
            <Button variant="outlined" onClick={() => setRotateOpen(true)} style={{ borderColor: "#C62828", color: "#C62828" }}>
              Rotate Key
            </Button>
          </>
        ) : (
          <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
            Get an API key from the{" "}
            <RouterLink to="/api-docs" style={{ color: DELTA_ACCENT }}>
              API docs page
            </RouterLink>
            .
          </Typography>
        )}
      </Paper>

      <Dialog open={rotateOpen} onClose={() => setRotateOpen(false)}>
        <DialogTitle>Rotate API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your current key stops working immediately and any application using it will need the new value. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateOpen(false)} disabled={rotating}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRotate} disabled={rotating} style={{ color: "#C62828" }}>
            {rotating ? "Rotating..." : "Rotate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(newApiKey)} onClose={() => setNewApiKey(undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Your new API key</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 16 }}>
            Copy this now - it won't be shown again.
          </DialogContentText>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TextField value={newApiKey} fullWidth size="small" InputProps={{ readOnly: true }} />
            <IconButton onClick={() => newApiKey && navigator.clipboard.writeText(newApiKey)}>
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewApiKey(undefined)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingPage;
